// Permissões de acesso às páginas do backoffice, configuráveis por papel.
//
// Papéis: 'manager' (Gestor) e 'funcionario' (Profissional) são configuráveis.
// O 'superuser' (Super Admin) tem SEMPRE acesso a tudo e não é configurável.
//
// A configuração é guardada por tenant em `configuracoes`, chave
// `permissoes_paginas`, como um JSON { chave: { manager, funcionario } }.
// As páginas em falta usam os defaults abaixo (que reproduzem o comportamento
// histórico: Agenda base para todos; gestão só Manager; Configurações só Super).

import { ESTADOS, ORDEM_ESTADOS } from './estados'

export const PAPEIS_CONFIG = [
  { chave: 'manager', label: 'Manager' },
  { chave: 'funcionario', label: 'Profissional' },
]

export const PAGINAS_PERMISSAO = [
  { chave: 'agenda_dia',    to: '/admin',                end: true, icon: '📅', label: 'Agenda Dia',  defaults: { manager: true,  funcionario: true  } },
  { chave: 'agenda_mes',    to: '/admin/mes',                       icon: '🗓️', label: 'Agenda Mês',  defaults: { manager: true,  funcionario: true  } },
  { chave: 'nova_marcacao', to: '/admin/nova-marcacao',             icon: '➕', label: 'Nova Marcação', defaults: { manager: true,  funcionario: true  } },
  { chave: 'bloqueios',     to: '/admin/bloqueios',                 icon: '🚫', label: 'Bloqueios',     defaults: { manager: true,  funcionario: true  } },
  { chave: 'clientes',      to: '/admin/clientes',                  icon: '👤', label: 'Clientes',      defaults: { manager: true,  funcionario: false } },
  { chave: 'promocoes',     to: '/admin/promocoes',                 icon: '📣', label: 'Promoções',     defaults: { manager: true,  funcionario: false } },
  { chave: 'profissionais', to: '/admin/profissionais',             icon: '👥', label: 'Profissionais', defaults: { manager: true,  funcionario: false } },
  { chave: 'servicos',      to: '/admin/servicos',                  icon: '💆', label: 'Serviços',      defaults: { manager: true,  funcionario: false } },
  { chave: 'salas',         to: '/admin/salas',                     icon: '🚪', label: 'Salas',         defaults: { manager: true,  funcionario: false } },
  { chave: 'relatorios',    to: '/admin/relatorios',                icon: '📊', label: 'Relatórios',    defaults: { manager: true,  funcionario: false } },
  { chave: 'faturacao',     to: '/admin/faturacao',                 icon: '🧾', label: 'Faturação',     defaults: { manager: true,  funcionario: false } },
  { chave: 'configuracoes', to: '/admin/configuracoes',             icon: '⚙️', label: 'Configurações', defaults: { manager: false, funcionario: false } },
]

// Ações/funcionalidades cujo acesso é configurável por papel (além das páginas).
export const ACOES_PERMISSAO = [
  { chave: 'eliminar_agendamento', icon: '🗑️', label: 'Eliminar marcações',                    defaults: { manager: true,  funcionario: false } },
  { chave: 'alterar_estado',       icon: '🔁', label: 'Alterar estado das marcações',           defaults: { manager: true,  funcionario: true  } },
  { chave: 'ver_agenda_todos',     icon: '👁️', label: 'Ver agenda de todos os profissionais',   defaults: { manager: true,  funcionario: false } },
  { chave: 'gerir_bloqueios',      icon: '🚫', label: 'Criar / eliminar bloqueios',             defaults: { manager: true,  funcionario: true  } },
  { chave: 'bloqueios_todos',      icon: '🏠', label: 'Bloquear o espaço / outros profissionais', defaults: { manager: true,  funcionario: false } },
  { chave: 'ver_valores',          icon: '💶', label: 'Ver valores das marcações (preço/sinal)', defaults: { manager: true,  funcionario: false } },
  { chave: 'editar_valores',       icon: '✏️', label: 'Editar valores das marcações',           defaults: { manager: true,  funcionario: false } },
  { chave: 'cancelar_fatura',      icon: '🧾', label: 'Cancelar faturas emitidas',              defaults: { manager: true,  funcionario: false } },
  { chave: 'editar_midia_servico', icon: '🎬', label: 'Editar apresentação dos serviços (vídeo/imagem)', defaults: { manager: true, funcionario: false } },
]

// Estados de marcação visíveis na Agenda (Dia e Mês), por papel.
// Default: Manager vê tudo; Profissional vê tudo menos os cancelados.
export const ESTADOS_AGENDA_PERMISSAO = ORDEM_ESTADOS.map(e => ({
  chave: e,
  icon: '●',
  cor: ESTADOS[e].cor,
  label: ESTADOS[e].label,
  descricao: ESTADOS[e].descricao,
  defaults: { manager: true, funcionario: e !== 'cancelado' },
}))

// Estados de marcação filtráveis no Relatório, por papel.
// Default: Manager filtra todos; Profissional só confirmado/concluído.
export const ESTADOS_RELATORIO_PERMISSAO = ORDEM_ESTADOS.map(e => ({
  chave: e,
  icon: '●',
  cor: ESTADOS[e].cor,
  label: ESTADOS[e].label,
  descricao: ESTADOS[e].descricao,
  defaults: { manager: true, funcionario: e === 'confirmado' || e === 'concluido' },
}))

// Estados de marcação visíveis no relatório de Faturação, por papel.
// Mesmos defaults do Relatório: Manager vê tudo; Profissional só o que realizou.
export const ESTADOS_FATURACAO_PERMISSAO = ORDEM_ESTADOS.map(e => ({
  chave: e,
  icon: '●',
  cor: ESTADOS[e].cor,
  label: ESTADOS[e].label,
  descricao: ESTADOS[e].descricao,
  defaults: { manager: true, funcionario: e === 'confirmado' || e === 'concluido' },
}))

// Constrói um mapa { chave: { manager, funcionario } } a partir do JSON
// guardado, preenchendo o que faltar com os defaults da lista indicada.
function construirMapa(lista, raw) {
  const mapa = {}
  for (const item of lista) {
    const guardado = (raw && raw[item.chave]) || {}
    mapa[item.chave] = {
      manager: typeof guardado.manager === 'boolean' ? guardado.manager : item.defaults.manager,
      funcionario: typeof guardado.funcionario === 'boolean' ? guardado.funcionario : item.defaults.funcionario,
    }
  }
  return mapa
}

export function permissoesComDefaults(raw) {
  return construirMapa(PAGINAS_PERMISSAO, raw)
}

export function acoesComDefaults(raw) {
  return construirMapa(ACOES_PERMISSAO, raw)
}

export function estadosAgendaComDefaults(raw) {
  return construirMapa(ESTADOS_AGENDA_PERMISSAO, raw)
}

export function estadosRelatorioComDefaults(raw) {
  return construirMapa(ESTADOS_RELATORIO_PERMISSAO, raw)
}

export function estadosFaturacaoComDefaults(raw) {
  return construirMapa(ESTADOS_FATURACAO_PERMISSAO, raw)
}
